
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/Hero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ContractorsList } from "@/components/ContractorsList";
import { Contractor } from "@/types/contractor";
import { LoadingState } from "@/components/LoadingState";
import { Card, CardContent } from "@/components/ui/card";
import { QuoteRequestForm } from "@/components/services/QuoteRequestForm";

const BestHVACContractorsPage = () => {
  const serviceType = "HVAC Contractors";
  const pageTitle = `Best ${serviceType} in Oklahoma City, OK`;

  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'hvac'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'HVAC')
        .eq('location', 'oklahoma-city')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }

      return data as Contractor[];
    },
  });

  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: serviceType, href: "/best-hvac-contractors-oklahoma-city" },
  ];

  if (isLoading) {
    return <LoadingState />;
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": pageTitle,
    "description": "Find top-rated, established HVAC contractors in Oklahoma City for air conditioning, heating, and ventilation services. Free quotes, trusted professionals.",
    "url": "https://homeprosokc.com/best-hvac-contractors-oklahoma-city",
  };

  return (
    <div className="min-h-screen">
      <SEO
        title={pageTitle}
        description="Find top-rated, established HVAC contractors in Oklahoma City for air conditioning, heating, and ventilation services. Free quotes, trusted professionals."
        canonicalUrl="/best-hvac-contractors-oklahoma-city"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title={pageTitle}
          subtitle="Connect with established, reputable HVAC professionals for all your heating, cooling, and ventilation needs."
          buttonText="Get a Quote"
          buttonLink="#quote-request"
        />

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Why Choose Our HVAC Contractors</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Established Professionals</h3>
                <p>Our HVAC contractors are well-established in the Oklahoma City area with proven experience and expertise.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Emergency Services</h3>
                <p>Many of our professionals offer 24/7 emergency HVAC services for when your system fails at the worst time.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Free Estimates</h3>
                <p>Get detailed, no-obligation quotes for your HVAC projects from our network of trusted professionals.</p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Our HVAC Services</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Heating Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Furnace installation and replacement</li>
                  <li>Heating system repairs</li>
                  <li>Heat pump services</li>
                  <li>Seasonal maintenance</li>
                  <li>Thermostat installation</li>
                  <li>Energy efficiency upgrades</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Cooling Services</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>AC installation and replacement</li>
                  <li>Air conditioning repairs</li>
                  <li>Ductwork services</li>
                  <li>Preventative maintenance</li>
                  <li>Smart thermostat installation</li>
                  <li>Indoor air quality solutions</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quote Request Form */}
        <QuoteRequestForm serviceName={pageTitle} />
        
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Top HVAC Contractors in Oklahoma City</h2>
          <ContractorsList
            contractors={contractors}
            isLoading={isLoading}
            viewMode="grid"
            onViewModeChange={() => {}}
            onServiceTypeChange={() => {}}
            onLocationChange={() => {}}
            selectedServiceType=""
          />
        </div>
      </div>
    </div>
  );
};

export default BestHVACContractorsPage;
